import React, { useEffect, useMemo, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { Scissors, Plus, X, ChevronLeft, ChevronRight, User, Phone, Trash2, Edit3, CheckCircle2 } from "lucide-react";

const STATUS = {
  scheduled: { bg: "#3B82F6", text: "Prenotato" },
  confirmed: { bg: "#8B5CF6", text: "Confermato" },
  in_progress: { bg: "#F59E0B", text: "In corso" },
  completed: { bg: "#2F9E77", text: "Completato" },
  cancelled: { bg: "#94A3B8", text: "Annullato" },
  no_show: { bg: "#DC2626", text: "No-show" },
};
const SLOTS = (() => {
  const s = [];
  for (let h = 8; h < 21; h++) for (let m = 0; m < 60; m += 30) s.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  return s;
})();
const WEEKDAY = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const toISODate = (d) => d.toISOString().slice(0, 10);
function mondayOf(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const wd = d.getDay();
  d.setDate(d.getDate() + (wd === 0 ? -6 : 1 - wd));
  return toISODate(d);
}
const fmtDay = (iso) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });
  } catch { return iso; }
};

const emptyForm = (day) => ({
  customer_name: "",
  customer_phone: "",
  operator: "",
  station: "",
  service_name: "",
  service_price: 0,
  duration_min: 30,
  start_at: `${day}T10:00:00`,
  notes: "",
  status: "scheduled",
  kind: "beauty",
});

export default function AppointmentsBeauty() {
  const [date, setDate] = useState(todayISO());
  const [calView, setCalView] = useState("week");
  const [appts, setAppts] = useState([]);
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(todayISO()));

  const weekMonday = useMemo(() => mondayOf(date), [date]);
  const weekDates = useMemo(() => [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(weekMonday, i)), [weekMonday]);

  const load = () => {
    const q = calView === "week"
      ? `/appointments?kind=beauty&from_date=${weekMonday}&to_date=${addDays(weekMonday, 6)}T23:59:59`
      : `/appointments?kind=beauty&date=${date}`;
    api.get(q).then((r) => setAppts(Array.isArray(r.data) ? r.data : [])).catch(() => setAppts([]));
  };
  useEffect(() => {
    api.get("/products?mode=parrucchiere").then((r) => setServices((Array.isArray(r.data) ? r.data : []).filter((p) => (p.duration_min || 0) > 0 || p.name)));
  }, []);
  useEffect(() => { load(); }, [date, calView, weekMonday]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of SLOTS) g[s] = [];
    for (const a of appts) {
      const t = (a.start_at || "").slice(11, 16);
      const slot = SLOTS.includes(t) ? t : SLOTS[0];
      g[slot].push(a);
    }
    return g;
  }, [appts]);

  const byDay = useMemo(() => {
    const m = {};
    for (const d of weekDates) m[d] = [];
    for (const a of appts) {
      const d = (a.start_at || "").slice(0, 10);
      if (!m[d]) m[d] = [];
      m[d].push(a);
    }
    return m;
  }, [appts, weekDates]);

  const openNew = (slot, dayIso) => {
    const day = dayIso || date;
    setEditing(null);
    const svc = services[0];
    setForm({
      ...emptyForm(day),
      start_at: `${day}T${slot || "10:00"}:00`,
      service_name: svc?.name || "",
      service_price: svc?.price || 0,
      duration_min: svc?.duration_min || 30,
    });
    setShowForm(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      customer_name: a.customer_name || a.owner_name || "",
      customer_phone: a.customer_phone || a.owner_phone || "",
      operator: a.operator || "",
      station: a.station || "",
      service_name: a.service_name || (a.services?.[0]?.name || ""),
      service_price: a.service_price || (a.services?.[0]?.price || 0),
      duration_min: a.duration_min || 30,
      start_at: a.start_at,
      notes: a.notes || "",
      status: a.status || "scheduled",
      kind: "beauty",
    });
    setShowForm(true);
  };
  const pickSvc = (s) => setForm({ ...form, service_name: s.name, service_price: s.price || 0, duration_min: s.duration_min || form.duration_min });
  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Nome cliente obbligatorio");
    try {
      const payload = { ...form, kind: "beauty", owner_name: form.customer_name, owner_phone: form.customer_phone };
      if (editing) await api.patch(`/appointments/${editing.id}`, payload);
      else await api.post("/appointments", payload);
      toast.success(editing ? "Aggiornato" : "Appuntamento creato");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  const del = async (a) => {
    if (!window.confirm(`Eliminare l'appuntamento di ${a.customer_name || a.owner_name}?`)) return;
    try {
      await api.delete(`/appointments/${a.id}`);
      toast.success("Eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-2">Estetica / Parrucchiere</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3">
            <Scissors className="w-8 h-8" /> Appuntamenti
          </h1>
          <p className="text-velta-mute mt-1">Persone, operatore, cabina. Non è il calendario animali.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-md border border-velta-border overflow-hidden h-10">
            <button type="button" data-testid="beauty-view-week" onClick={() => setCalView("week")} className={`px-3 text-xs font-medium ${calView === "week" ? "bg-velta-ink text-white" : "bg-white text-velta-mute"}`}>Settimana</button>
            <button type="button" data-testid="beauty-view-day" onClick={() => setCalView("day")} className={`px-3 text-xs font-medium ${calView === "day" ? "bg-velta-ink text-white" : "bg-white text-velta-mute"}`}>Giorno</button>
          </div>
          <button data-testid="beauty-prev" onClick={() => setDate(calView === "week" ? addDays(weekMonday, -7) : addDays(date, -1))} className="h-10 w-10 border border-velta-border rounded-md flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          {calView === "day" ? (
            <input type="date" data-testid="beauty-date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 px-3 border border-velta-border rounded-md text-sm" />
          ) : (
            <div className="h-10 px-3 border border-velta-border rounded-md text-sm flex items-center font-medium">{weekDates[0].slice(8, 10)}–{weekDates[6].slice(8, 10)}/{weekDates[6].slice(5, 7)}</div>
          )}
          <button data-testid="beauty-next" onClick={() => setDate(calView === "week" ? addDays(weekMonday, 7) : addDays(date, 1))} className="h-10 w-10 border border-velta-border rounded-md flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          <button type="button" onClick={() => setDate(todayISO())} className="h-10 px-3 border border-velta-border rounded-md text-xs">Oggi</button>
          <button data-testid="beauty-new" onClick={() => openNew("10:00")} className="h-10 px-4 bg-velta-ink text-white rounded-md text-sm font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Nuovo</button>
        </div>
      </div>
      <div className="mb-4 text-sm font-medium capitalize">{calView === "day" ? fmtDay(date) : "Settimana"}</div>

      {calView === "week" ? (
        <div className="bg-white border border-velta-border rounded-md overflow-x-auto" data-testid="beauty-week-grid">
          <div className="grid grid-cols-7 min-w-[840px] divide-x divide-velta-border">
            {weekDates.map((d, i) => {
              const items = byDay[d] || [];
              return (
                <div key={d} className={`min-h-[380px] flex flex-col ${d === todayISO() ? "bg-amber-50/40" : ""}`}>
                  <button type="button" onClick={() => { setDate(d); setCalView("day"); }} className={`px-2 py-2 text-left border-b ${d === date ? "bg-velta-ink text-white" : "bg-velta-bg"}`}>
                    <div className="text-[10px] font-mono uppercase">{WEEKDAY[i]}</div>
                    <div className="font-heading font-semibold text-lg">{d.slice(8, 10)}</div>
                    <div className="text-[10px] font-mono">{items.length} app.</div>
                  </button>
                  <div className="p-1.5 space-y-1.5 flex-1">
                    {items.map((a) => (
                      <button key={a.id} type="button" onClick={() => openEdit(a)} className="w-full text-left rounded border px-1.5 py-1.5" style={{ borderLeftWidth: 4, borderColor: (STATUS[a.status] || STATUS.scheduled).bg }}>
                        <div className="text-[10px] font-mono text-velta-mute">{(a.start_at || "").slice(11, 16)}</div>
                        <div className="text-xs font-semibold truncate">{a.customer_name || a.owner_name || "—"}</div>
                        <div className="text-[10px] text-velta-mute truncate">{a.service_name || a.operator || ""}</div>
                      </button>
                    ))}
                    <button type="button" onClick={() => openNew("10:00", d)} className="w-full h-8 border border-dashed rounded text-[10px] text-velta-mute">+ Prenota</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-velta-border rounded-md overflow-hidden">
          {SLOTS.map((slot, i) => {
            const items = grouped[slot] || [];
            return (
              <div key={slot} className={`grid grid-cols-[80px_1fr] min-h-[52px] border-b border-velta-border/60 ${i % 2 ? "bg-velta-bg/40" : ""}`}>
                <div className="p-3 text-xs font-mono text-velta-mute border-r">{slot}</div>
                <div className="p-2 flex flex-wrap gap-2">
                  {items.length ? items.map((a) => (
                    <div key={a.id} className="border-2 rounded-md p-2.5 min-w-[240px] flex-1 max-w-md" style={{ borderColor: (STATUS[a.status] || STATUS.scheduled).bg }}>
                      <div className="flex justify-between gap-2">
                        <div>
                          <div className="font-semibold flex items-center gap-1"><User className="w-4 h-4" /> {a.customer_name || a.owner_name}</div>
                          {(a.customer_phone || a.owner_phone) && <div className="text-[11px] text-velta-mute flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {a.customer_phone || a.owner_phone}</div>}
                          <div className="text-xs mt-1">{a.service_name}{a.operator ? ` · ${a.operator}` : ""}{a.station ? ` · ${a.station}` : ""}</div>
                        </div>
                        <span className="text-[9px] font-mono text-white px-1.5 py-0.5 rounded h-fit" style={{ background: (STATUS[a.status] || STATUS.scheduled).bg }}>{(STATUS[a.status] || STATUS.scheduled).text}</span>
                      </div>
                      <div className="flex gap-1 mt-2">
                        <button type="button" onClick={() => openEdit(a)} className="h-7 w-7 rounded hover:bg-velta-bg flex items-center justify-center"><Edit3 className="w-3.5 h-3.5" /></button>
                        {a.status !== "completed" && (
                          <button type="button" onClick={() => api.patch(`/appointments/${a.id}/status?status=completed`).then(load)} className="h-7 w-7 rounded hover:bg-velta-bg text-velta-success flex items-center justify-center"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                        )}
                        <button type="button" onClick={() => del(a)} className="h-7 w-7 rounded hover:bg-red-50 text-velta-error ml-auto flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  )) : (
                    <button type="button" onClick={() => openNew(slot)} className="w-full h-10 border border-dashed rounded text-[11px] text-velta-mute">+ Prenota</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h3 className="font-heading text-2xl font-semibold">{editing ? "Modifica" : "Nuovo appuntamento"}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="beauty-form-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nome cliente *" className="w-full h-11 px-3 border rounded-md" />
              <input data-testid="beauty-form-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="Telefono" className="w-full h-11 px-3 border rounded-md font-mono" />
              <div className="grid grid-cols-2 gap-2">
                <input value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} placeholder="Operatore" className="h-11 px-3 border rounded-md" />
                <input value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })} placeholder="Cabina / poltrona" className="h-11 px-3 border rounded-md" />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-velta-mute mb-1">Servizio</div>
                <div className="flex flex-wrap gap-1.5">
                  {services.map((s) => (
                    <button key={s.id} type="button" onClick={() => pickSvc(s)} className={`text-xs px-2 h-8 rounded-md border ${form.service_name === s.name ? "bg-velta-ink text-white border-velta-ink" : "border-velta-border"}`}>
                      {s.name} · {formatEUR(s.price)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="datetime-local" value={(form.start_at || "").slice(0, 16)} onChange={(e) => setForm({ ...form, start_at: `${e.target.value}:00` })} className="h-11 px-3 border rounded-md" />
                <input type="number" min={15} step={15} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value, 10) || 30 })} className="h-11 px-3 border rounded-md" />
              </div>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note, allergie, preferenze…" className="w-full p-3 border rounded-md text-sm" />
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 h-11 border rounded-md">Annulla</button>
                <button type="button" data-testid="beauty-form-save" onClick={save} className="flex-1 h-11 bg-velta-ink text-white rounded-md">Salva</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
